// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/OAuthFlowSuccess.jsx
//
// Route component for /oauth/success.
//
// Mounted as a plain <Route> in App.js, NOT a <PrivateRoute>. The
// page is deliberately unauthenticated:
//
//   • The backend's /oauth/callback endpoint is unauthenticated too
//     (Google is the caller and cannot send our bearer token), so
//     treating the tail of the flow as authenticated in the frontend
//     would be inconsistent.
//   • The page reads only URL query params and sessionStorage. It
//     makes zero authenticated backend calls.
//   • If the tenant's token expired during the flow, or if their
//     browser partitioned localStorage across the cross-site
//     navigation to accounts.google.com (Safari iOS, Firefox TCP),
//     a PrivateRoute guard would bounce them to /login and hide the
//     confirmation even though the connection *was* successfully
//     created on the backend.
//
// Nothing on this page is sensitive; the external_account_id is
// already in the address bar, and the "Done" button lands on "/"
// which is a PrivateRoute and will redirect to /login for an
// unauthenticated visitor.
//
// The backend's /oauth/callback handler redirects here after a
// successful consent, with three query params:
//
//   provider=<name>
//   external_account_id=<stable provider id>
//   display_name=<human-readable account label>
//
// The "Done" button navigates back to the app the user came from.
// We recover the app from sessionStorage, which the Connections
// card wrote just before navigating to /oauth/start. If it's
// missing (user opened this URL in a fresh tab, or cleared storage),
// we fall back to the admin home.

import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";

// Reuse a small subset of the same styles so the flow pages match
// the card visually. Not ideal to import across components, but
// both surfaces are conceptually the same feature and the CSS is
// small.
const successStyles = {
  page: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px",
  },
  card: {
    maxWidth: 480,
    width: "100%",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "32px 28px",
    textAlign: "center",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
  },
  icon: {
    color: "#38a169",
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "#1a202c",
    margin: "0 0 8px 0",
  },
  body: {
    fontSize: 14,
    color: "#4a5568",
    lineHeight: 1.6,
    margin: "0 0 8px 0",
  },
  accountBox: {
    margin: "16px 0 20px 0",
    padding: "12px 14px",
    background: "#f7fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    textAlign: "left",
  },
  accountLine: {
    fontSize: 13,
    color: "#2d3748",
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
  },
  accountKey: {
    color: "#718096",
    marginRight: 12,
  },
  accountValue: {
    fontWeight: 500,
    wordBreak: "break-all",
    textAlign: "right",
  },
  button: {
    padding: "10px 24px",
    fontSize: 14,
    fontWeight: 600,
    color: "#ffffff",
    background: "#4299e1",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
};

export default function OAuthFlowSuccess() {
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const provider = params.get("provider") || "the provider";
  const externalAccountId = params.get("external_account_id") || "";
  const displayName = params.get("display_name") || "";

  // Recover the app the user came from. The Connections card writes
  // this to sessionStorage right before navigating away to
  // /oauth/start, so by the time we're here we have it back.
  //
  // sessionStorage (not localStorage) because this is per-tab state
  // — a user who opens two admin tabs and connects Google in each
  // shouldn't have one tab's app leak into the other's success page.
  //
  // This read is best-effort. If sessionStorage was wiped (partitioned
  // browsers, private mode, user cleared it) or the app was never
  // written (user hit the URL directly), we fall back to "/" in
  // handleDone. Not an error.
  const [returnApp, setReturnApp] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("oauth_return_to");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.region && parsed?.appName) {
          setReturnApp(parsed);
          return;
        }
      }
    } catch (_) {
      // ignore — will fall back to "/" in handleDone
    }
  }, []);

  const handleDone = () => {
    if (returnApp?.region && returnApp?.appName) {
      // Send the user back to the app they came from and re-open
      // the Connections tab. This matches where they were before
      // the flow started, so the mental model is "back where I was,
      // with a fresh connection."
      //
      // The second argument to history.push is a location state
      // object. AppDetailPage reads state.activeItemId on mount and
      // sets its own active tab accordingly — so the admin lands on
      // the Connections card directly, not the default tab.
      //
      // Note: the route path used here is /:region/:app_name/app-detail.
      // That matches the PrivateRoute registered in App.js. If the
      // admin route ever changes, this is one of the two places to
      // update (the other is OAuthFlowError).
      history.push(
        `/${returnApp.region}/${returnApp.appName}/app-detail`,
        { activeItemId: "Connections" },
      );
    } else {
      // Fallback — navigate to the admin root. That path is a
      // PrivateRoute, so an unauthenticated visitor will be bounced
      // to /login. An authenticated visitor lands on the app list.
      history.push("/");
    }
  };

  return (
    <div style={successStyles.page}>
      <div style={successStyles.card}>
        <FaCheckCircle size={48} style={successStyles.icon} />

        <h1 style={successStyles.title}>Connected</h1>

        <p style={successStyles.body}>
          Your {provider.charAt(0).toUpperCase() + provider.slice(1)} account
          is now linked to this app.
        </p>

        {(displayName || externalAccountId) && (
          <div style={successStyles.accountBox}>
            {displayName && (
              <div style={successStyles.accountLine}>
                <span style={successStyles.accountKey}>Account</span>
                <span style={successStyles.accountValue}>{displayName}</span>
              </div>
            )}
            {externalAccountId && externalAccountId !== displayName && (
              <div style={successStyles.accountLine}>
                <span style={successStyles.accountKey}>ID</span>
                <span style={successStyles.accountValue}>
                  {externalAccountId}
                </span>
              </div>
            )}
          </div>
        )}

        <p
          style={{
            ...successStyles.body,
            fontSize: 13,
            color: "#718096",
            marginBottom: 20,
          }}
        >
          Skills and forms on this app can now act on behalf of this account.
          You can disconnect it at any time from the Connections screen.
        </p>

        <button
          type="button"
          style={successStyles.button}
          onClick={handleDone}
        >
          Done
        </button>
      </div>
    </div>
  );
}